import { Unit, UnitsState } from '@/types/units';

const isStudent = (): boolean => localStorage.getItem('userRole') === 'student';

/** Students: only auth/user sync across tabs; level/unit/topic stay per tab. */
export const shouldSyncReduxAction = (actionType: string): boolean => {
  if (actionType.startsWith('Auth/') || actionType.startsWith('user/')) {
    return true;
  }

  if (isStudent()) {
    return false;
  }

  if (actionType.startsWith('units/fetchUnits')) {
    return false;
  }

  if (
    actionType.startsWith('units/') ||
    actionType.startsWith('appSettings/') ||
    actionType.startsWith('refreshTopicQuizzes/')
  ) {
    return true;
  }

  return false;
};

/** After user/ sync refetches units in this tab, do not jump to the first unit. */
export const mergeSelectedUnitAfterFetchUnits = (
  currentSelectedUnit: UnitsState['selectedUnit'],
  sortedUnits: Unit[]
): UnitsState['selectedUnit'] => {
  if (!isStudent()) {
    return {
      id: sortedUnits[0]?.id,
      name: sortedUnits[0]?.name,
      order: sortedUnits[0]?.order,
    };
  }

  const currentUnitId = currentSelectedUnit?.id;
  const preservedUnit =
    currentUnitId != null &&
    currentUnitId > 0 &&
    sortedUnits.find(unit => unit.id === currentUnitId);

  if (preservedUnit) {
    return {
      id: preservedUnit.id,
      name: preservedUnit.name,
      order: preservedUnit.order,
    };
  }

  if (!currentUnitId || currentUnitId <= 0) {
    return {
      id: sortedUnits[0]?.id,
      name: sortedUnits[0]?.name,
      order: sortedUnits[0]?.order,
    };
  }

  return currentSelectedUnit;
};

const findUnitIdByTopicId = (
  topics: UnitsState['topics'],
  topicId: number | undefined
): number | undefined => {
  if (!topicId) {
    return undefined;
  }

  const entry = Object.entries(topics).find(([, unitTopics]) =>
    unitTopics?.some(t => t.id === topicId)
  );

  return entry ? Number(entry[0]) : undefined;
};

/** Progress submit + isLastActivityInTopic: students use topic's unit, not stale selectedUnit. */
export const resolveProgressUnitId = (params: {
  isTeacher: boolean;
  topics: UnitsState['topics'];
  topicId: number | undefined;
  selectedUnitId: number | undefined;
}): number | undefined => {
  const { isTeacher, topics, topicId, selectedUnitId } = params;

  if (isTeacher) {
    return selectedUnitId;
  }

  return findUnitIdByTopicId(topics, topicId) ?? selectedUnitId;
};
